import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n-lite';
import * as bookActions from '../../actions/bookActions';
import * as accountActions from '../../actions/accountActions';
import { translateObject } from '../../services/translate/customTranslateService';
import * as bookService from '../../services/domain/bookService';
import { isLinkActivity, isPdfOrLinkActivity } from '../../services/domain/bookService';
import { DRAWER_CONTENTS, ACTIVITY_TYPE, ROUTES, ROUTES_CONSTANTS, MAX_LENGTH_VARIABLES } from '../../const';
import * as drawerActions from '../../actions/drawerActions';
import * as optionItemsFactory from '../../factory/optionItemsFactory';
import GroupAssignedTile from '../common/GroupAssignedTile';
import CustomTooltip from '../common/CustomTooltip';
import * as gradebookActions from '../../actions/classroom/gradebookActions';
import routerHistory from './../../routerHistory';
import * as googleClassroomActions from '../../actions/googleClassroomActions';
import { isElectronApp } from '../../services/crossPlatform/crossPlatformService';
import MoreOptions from '../common/sheets/MoreOptions';
import EditNameSheet from '../common/sheets/EditNameSheet';
import { isMobileViewport } from '../../utils/mobileUtils';
import LinkedContentTrigger from './desynchronizeBook/LinkedContentTrigger';
import * as messageAlert from '../../actions/messageAlertAction';
import { ANALYTICS_CONTEXT } from '../../types/analytics';

const LESSON_CARD_TITLE = 'lesson-card-title';
const LESSON_CARD_TITLE_DISABLED = 'lesson-card-title disabled';
const IMAGE_DISABLED = 'disabled';
const LESSON_CARD_CONTENT = 'lesson-card-content';
const LESSON_CARD_CONTENT_DISABLED = 'lesson-card-content disabled';

class LessonCard extends React.Component {
  state = {
    isEditing: false,
    isEditSheetOpen: false,
    name: translateObject(this.props.lesson.name, this.props.language)
  };

  componentDidUpdate(prevProps) {
    const { lesson, language } = this.props;
    const newLessonName = translateObject(lesson.name, language);
    const oldLessonName = translateObject(prevProps.lesson.name, prevProps.language);
    if (oldLessonName !== newLessonName) {
      this.setState({
        name: newLessonName
      });
    }
  }

  openLessonContent = () => {
    const { accountActions, bookActions, book, unit, lesson, account, institution, classroom } = this.props;
    accountActions.forceCheckAndRefreshToken(() => {
      bookActions.openResource({ book, unit, lesson, account, institution, classroom });
    });
  };

  launchSmartContentHandler = e => {
    const { bookActions, isDragging, isPreview } = this.props;
    const { isEditing } = this.state;

    e.preventDefault();

    if (!isDragging && !isPreview && !isEditing) {
      const { lesson } = this.props;

      if (isLinkActivity(lesson.format)) {
        bookActions.openLink(lesson.url);
      } else if (this.isAdaptation()) {
        this.handleAdaptationClick();
      } else {
        this.openLessonContent();
      }
    }
  };

  // Students (Vista Mobile 2a): the whole card opens the lesson. The image and title handlers
  // already call preventDefault, so this only acts on the rest of the card.
  handleCardClick = e => {
    if (!e.defaultPrevented && isMobileViewport()) {
      this.launchSmartContentHandler(e);
    }
  };

  isAdaptation = () => {
    const { lesson } = this.props;
    return Boolean(lesson.adaptation && lesson.adaptation.isAdaptation);
  };

  isPdfAdaptation = () => {
    const { lesson } = this.props;
    return this.isAdaptation() && lesson.adaptation.type === 'pdf';
  };

  isPendingAdaptation = () => {
    const { lesson } = this.props;
    return this.isAdaptation() && lesson.adaptation.status === 'pending';
  };

  isPdfOrLinkLesson = () => {
    const { lesson } = this.props;
    return !this.isAdaptation() && isPdfOrLinkActivity(lesson.format);
  };

  shouldHideCopyAndGradebookOptions = () => {
    return this.isPdfAdaptation() || this.isPdfOrLinkLesson();
  };

  shouldHideNeaeAdaptationsOption = () => {
    return this.isAdaptation() || this.isPdfOrLinkLesson();
  };

  getLessonFormat = () => {
    const { lesson } = this.props;

    if (this.isPdfAdaptation()) {
      return 'application/pdf';
    }

    return lesson.format;
  };

  handleAdaptationClick = () => {
    const { bookActions, messageAlert, lesson } = this.props;

    if (lesson.adaptation && lesson.adaptation.status === 'pending') {
      messageAlert.error(I18n.t('rightPanel.neaeAdaptations.info'));
      return;
    }

    const { adaptation } = lesson;

    if (adaptation.type === 'pdf' && adaptation.pdfUrl) {
      bookActions.openLink(adaptation.pdfUrl);
      return;
    }

    if (adaptation.type === 'content') {
      this.openLessonContent();
    }
  };

  assignContent = () => {
    const { isAppOnline, bookActions, lesson, drawerActions, book, unit } = this.props;
    if (isAppOnline) {
      bookActions.setSelectedResource(
        Object.assign(
          {
            isLesson: true,
            bookAssign: book.configuration,
            unitAssign: unit.configuration
          },
          lesson
        )
      );
      drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.ASSIGN_STUDENTS);
    }
  };

  openNeaeAdaptationsPanel = () => {
    const { isAppOnline, bookActions, lesson, drawerActions, book, unit } = this.props;
    if (isAppOnline) {
      bookActions.setSelectedResource(
        Object.assign(
          {
            isLesson: true,
            bookAssign: book.configuration,
            unitAssign: unit.configuration,
            unitActivityId: unit.activityId
          },
          lesson
        )
      );
      drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.NEAE_ADAPTATIONS_PANEL);
    }
  };

  handleLessonVisibility = () => {
    const { isAppOnline } = this.props;
    if (isAppOnline) {
      const { bookActions, lesson, idClassroom, book } = this.props;

      let activityId = lesson.activityId;
      let bookId = book.id;
      let classroomId = idClassroom;

      if (lesson.disabled) {
        bookActions.enableBookLevel(classroomId, bookId, activityId, ANALYTICS_CONTEXT.LESSON_SHOW);
      } else {
        bookActions.disableBookLevel(classroomId, bookId, activityId, ANALYTICS_CONTEXT.LESSON_HIDE);
      }
    }
  };

  getOptionItems = () => {
    const {
      lesson,
      authoring,
      // deleteCatalogContentAllowed,
      deleteCatalogLessonAllowed,
      menuRoutes,
      allowShareInGoogleClassroom,
      isAppOnline,
      enableNeaeAdaptationsCustomer,
      enableNeaeAdaptationsTeacher
    } = this.props;
    let visibilityText = lesson.disabled ? I18n.t('common.option.show') : I18n.t('common.option.hide');
    let visibilityIcon = lesson.disabled ? 'custom-icons-eye-view' : 'custom-icons-no-eye-view';
    let ret = [];
    if (!authoring) {
      return ret;
    }
    if (!this.isAdaptation()) {
      ret.push(optionItemsFactory.getAssignItem(this.assignContent, isAppOnline));
    }
    if (menuRoutes.includes(ROUTES_CONSTANTS.GRADEBOOK) && !this.shouldHideCopyAndGradebookOptions()) {
      ret.push(optionItemsFactory.seeGradesItem(this.goToGradebook, isAppOnline));
    }
    ret.push(optionItemsFactory.getEditItem(this.editLesson, isAppOnline));
    if (!this.shouldHideCopyAndGradebookOptions()) {
      ret.push(optionItemsFactory.getCopyItem(this.cloneLesson, isAppOnline));
    }
    if (allowShareInGoogleClassroom && !isElectronApp()) {
      ret.push(optionItemsFactory.shareInGoogleClassroom(this.selectCourseToShareInGoogle, isAppOnline));
    }
    if (!this.isPendingAdaptation()) {
      ret.push(
        optionItemsFactory.getVisibilityItem(visibilityText, visibilityIcon, this.handleLessonVisibility, isAppOnline)
      );
    }
    if (deleteCatalogLessonAllowed || !lesson.fromCatalog) {
      ret.push(optionItemsFactory.getDeleteItem(this.deleteActivity.bind(this), isAppOnline));
    }
    if (enableNeaeAdaptationsCustomer && enableNeaeAdaptationsTeacher && !this.shouldHideNeaeAdaptationsOption()) {
      ret.push(optionItemsFactory.getNeaeAdaptationsItem(this.openNeaeAdaptationsPanel, isAppOnline));
    }
    return ret;
  };

  goToGradebook = () => {
    const { idClassroom, book, unit, lesson, gradebookActions } = this.props;
    gradebookActions.setData(idClassroom, book, unit, lesson);
    routerHistory.push(ROUTES.GRADEBOOK.replace('_classId_', idClassroom));
  };

  deleteActivity = () => {
    const { isAppOnline } = this.props;
    if (isAppOnline) {
      const { bookActions, lesson } = this.props;
      bookActions.setDeleteLessonData(lesson);
    }
  };

  editLesson = () => {
    if (isMobileViewport()) {
      this.setState({ isEditSheetOpen: true });
      return;
    }
    this.setState(
      {
        isEditing: true
      },
      () => {
        this.props.changeDrag(true);
        this.lessonTextarea.focus();
      }
    );
  };

  cloneLesson = () => {
    const { isAppOnline } = this.props;
    if (isAppOnline) {
      const { bookActions, lesson, idClassroom, book, unit } = this.props;
      bookActions.cloneLesson(lesson, idClassroom, book.id, unit.activityId, ANALYTICS_CONTEXT.LESSON_CLONE_CARD_BUTTON);
    }
  };

  selectCourseToShareInGoogle = () => {
    const { drawerActions, lesson, googleClassroomActions } = this.props;
    googleClassroomActions.setContent(lesson, ACTIVITY_TYPE.LESSON);
    drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.SELECT_COURSE_GOOGLE);
  };

  getLessonImgByMimeType = mimeType => {
    const { placeholdersImages } = this.props;

    if (placeholdersImages) {
      const lessonsPlaceHolder = placeholdersImages.lesson;
      return lessonsPlaceHolder[mimeType] || lessonsPlaceHolder.default;
    }

    return '';
  };

  getLessonImageUrl = () => {
    const { lesson } = this.props;
    return lesson.imageUrl || this.getLessonImgByMimeType(this.getLessonFormat());
  };

  getLessonImage = visibilityImage => {
    return (
      <img
        className={visibilityImage}
        src={this.getLessonImageUrl()}
        onClick={this.launchSmartContentHandler}
        alt="presentation"
        draggable="false"
      />
    );
  };

  handleKeyPress = ev => {
    const { messageAlert } = this.props;
    const name = this.state.name;
    const nameMaxLength = MAX_LENGTH_VARIABLES.LESSON_NAME;

    if (ev.key === 'Enter') {
      ev.preventDefault();

      if (name.length > nameMaxLength) {
        messageAlert.error(
          I18n.t('error.valueLength', { variable: I18n.t('common.lessonTitle'), length: nameMaxLength })
        );
        return;
      } else {
        this.updateName();
        messageAlert.success(`"${name}" ${I18n.t('common.bookSaved')}`);
      }
    }
  };

  clickOut = () => {
    const { messageAlert } = this.props;
    const name = this.state.name;
    const nameMaxLength = MAX_LENGTH_VARIABLES.LESSON_NAME;

    if (name.length > nameMaxLength) {
      messageAlert.error(
        I18n.t('error.valueLength', { variable: I18n.t('common.lessonTitle'), length: nameMaxLength })
      );
      return;
    } else {
      this.updateName();
      messageAlert.success(`"${name}" ${I18n.t('common.bookSaved')}`);
    }
  };

  saveNameFromSheet = name => {
    const { messageAlert } = this.props;
    this.setState({ name, isEditSheetOpen: false }, () => {
      this.updateName();
      messageAlert.success(`"${name}" ${I18n.t('common.bookSaved')}`);
    });
  };

  handleChangeName = ev => {
    this.setState({ name: ev.target.value });
  };

  updateName = () => {
    const { lesson, book, classroom, bookActions, changeDrag } = this.props;
    let updatedUnitName = this.state.name;

    bookActions.updateUnitName(book.id, classroom.id, lesson.activityId, updatedUnitName).then(() => {
      this.setState({
        isEditing: false
      });
    });
    changeDrag(false);
  };

  renderTooltipWrapper = (title, children) => {
    return (
      <CustomTooltip title={children} classes="" placement="bottom">
        <div className="name"> {title} </div>
      </CustomTooltip>
    );
  };

  render() {
    const { lesson, isComplete, isAppOnline, groups, role, classroom } = this.props;
    const { isEditing, isEditSheetOpen } = this.state;
    const optionItems = this.getOptionItems();

    const isLink = isLinkActivity(lesson.format);
    let showTooltip = false;
    const maxLetter = 38;
    if (this.state.name.length > maxLetter) {
      showTooltip = true;
    }

    let lessonCardTitle = lesson.disabled || (!isAppOnline && isLink) ? LESSON_CARD_TITLE_DISABLED : LESSON_CARD_TITLE;
    let visibilityImage = lesson.disabled || (!isAppOnline && isLink) ? IMAGE_DISABLED : '';
    let visibleBackground =
      lesson.disabled || (!isAppOnline && isLink) ? LESSON_CARD_CONTENT : LESSON_CARD_CONTENT_DISABLED;
    let instructorContentVisibility = { visibility: isComplete ? 'visible' : 'hidden' };

    const lessonActivityId = bookService.removeNotAllowedCharactersToActivityId(lesson.activityId);

    const isTitleEditable = isEditing && isComplete;
    const titleAsDiv = (
      <div className={lessonCardTitle}>
        {showTooltip
          ? this.renderTooltipWrapper(`${this.state.name.substring(0, maxLetter)}...`, this.state.name)
          : this.state.name}
      </div>
    );
    const titleAsTextarea = (
      <textarea
        className={lessonCardTitle}
        ref={ref => (this.lessonTextarea = ref)}
        spellCheck="false"
        value={this.state.name}
        onChange={this.handleChangeName}
        onKeyPress={this.handleKeyPress}
        onBlur={this.clickOut}
      />
    );

    return (
      <div className="row-lesson-card no-user-selection">
        <div className="lesson-card" onClick={isComplete ? undefined : this.handleCardClick}>
          {this.getLessonImage(visibilityImage)}
          <div className={visibleBackground}>
            <div className="lesson-card-wrapper">
              <div onClick={this.launchSmartContentHandler}>{isTitleEditable ? titleAsTextarea : titleAsDiv}</div>
              <div className={`lesson-card-options ${isComplete ? '' : 'student'}`} style={instructorContentVisibility}>
                <MoreOptions
                  id={`lesson-options-${lessonActivityId}`}
                  color="gray"
                  dropdownSide="left"
                  options={optionItems}
                  sheetTitle={this.state.name}
                  sheetOverline={I18n.t('mobileSheets.lesson')}
                  sheetMedia={<img className="sheet-media-image" src={this.getLessonImageUrl()} alt="" />}
                />
              </div>
              {!isComplete && <i className="custom-icons-no-interactive lesson-card-open-icon" aria-hidden="true" />}
            </div>

            <div className="lesson-card-footer">
              <GroupAssignedTile assignedGroup={lesson.configuration} groups={groups} role={role} />
              <LinkedContentTrigger
                activityId={lesson.activityId}
                sectionId={classroom?.sectionId}
                linked={lesson.linked}
              />
            </div>
          </div>
        </div>
        {isComplete && (
          <EditNameSheet
            open={isEditSheetOpen}
            title={I18n.t('mobileSheets.editLesson')}
            initialValue={this.state.name}
            maxLength={MAX_LENGTH_VARIABLES.LESSON_NAME}
            onSave={this.saveNameFromSheet}
            onClose={() => this.setState({ isEditSheetOpen: false })}
          />
        )}
      </div>
    );
  }
}

LessonCard.propTypes = {
  isDragging: PropTypes.bool,
  isAppOnline: PropTypes.any,
  bookActions: PropTypes.any,
  accountActions: PropTypes.any,
  lesson: PropTypes.any,
  drawerActions: PropTypes.any,
  gradebookActions: PropTypes.any,
  account: PropTypes.any,
  classroom: PropTypes.any,
  institution: PropTypes.any,
  book: PropTypes.any,
  unit: PropTypes.any,
  isPreview: PropTypes.any,
  language: PropTypes.any,
  idClassroom: PropTypes.any,
  isComplete: PropTypes.any,
  groups: PropTypes.any,
  authoring: PropTypes.any,
  placeholdersImages: PropTypes.any,
  changeDrag: PropTypes.any,
  deleteCatalogLessonAllowed: PropTypes.any,
  role: PropTypes.string,
  messageAlert: PropTypes.object,
  menuRoutes: PropTypes.any,
  enableNeaeAdaptationsCustomer: PropTypes.bool,
  enableNeaeAdaptationsTeacher: PropTypes.bool
};

function mapStateToProps(state) {
  return {
    account: state.account,
    book: state.book.book,
    groups: state.classrooms.selectedClassroom.groups,
    classroom: state.classrooms.selectedClassroom.classroom,
    institution: state.school.school,
    language: state.account.user.language,
    i18n: state.i18n,
    isAppOnline: state.connection.status.online,
    placeholdersImages: state.theme.images.placeholders,
    authoring: state.account.configuration.authoring,
    deleteCatalogLessonAllowed: state.account.configuration.deleteCatalogLessonAllowed,
    allowShareInGoogleClassroom: state.account.configuration.allowShareInGoogleClassroom,
    role: state.classrooms.selectedClassroom.classroom.role,
    menuRoutes: state.theme.layoutSettings.teacherSettings.classroomSections.menuRoutes,
    enableNeaeAdaptationsCustomer: state.account.configuration.enableNeaeAdaptations,
    enableNeaeAdaptationsTeacher: state.account.userConfiguration.enableNeaeAdaptations
  };
}

function mapDispatchToProps(dispatch) {
  return {
    bookActions: bindActionCreators(bookActions, dispatch),
    accountActions: bindActionCreators(accountActions, dispatch),
    drawerActions: bindActionCreators(drawerActions, dispatch),
    gradebookActions: bindActionCreators(gradebookActions, dispatch),
    googleClassroomActions: bindActionCreators(googleClassroomActions, dispatch),
    messageAlert: bindActionCreators(messageAlert, dispatch),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LessonCard);
